import { ChangeDetectionStrategy, Component, effect } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  OiqFormInputComponent,
  OiqFormPhoneComponent,
  OiqFormPhoneLengthValidator,
} from '@oiq-ui-sdk/form';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import { CollectionFilterFormatPayloadInput, CollectionFilterFromQueryInput } from './filter.utils';

@Component({
  selector: 'collection-filter-input',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormInputComponent, OiqFormPhoneComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let _definition = definition();
    @if (_definition) {
      @switch (_definition.input.type) {
        @case ('phone') {
          <!-- Use OiqFormPhoneComponent for phone (has built-in mask) -->
          <oiq-form-phone
            [placeholder]="_definition.placeholder || _definition.label"
            [formControl]="phoneControl"
            [canClear]="_definition.input.canClear"
            [prefixIcon]="_definition.input.prefixIcon"
            [suffixIcon]="_definition.input.suffixIcon"
            [enterSubmit]="false"
            (event)="onValueChange($event)"
            [class.mb-2]="phoneControl.invalid && (phoneControl.dirty || phoneControl.touched)">
          </oiq-form-phone>
        }
        @case ('email') {
          <!-- Use oiq-form-input with type="email" for email -->
          <oiq-form-input
            type="email"
            [placeholder]="_definition.placeholder || _definition.label"
            [formControl]="emailControl"
            [canClear]="_definition.input.canClear"
            [prefixIcon]="_definition.input.prefixIcon"
            [suffixIcon]="_definition.input.suffixIcon"
            [enterSubmit]="false"
            (event)="onValueChange($event)"
            [class.mb-2]="emailControl.invalid && (emailControl.dirty || emailControl.touched)">
          </oiq-form-input>
        }
        @default {
          <oiq-form-input
            type="text"
            [placeholder]="_definition.placeholder || _definition.label"
            [formControl]="internalControl"
            [canClear]="_definition.input.canClear"
            [prefixIcon]="_definition.input.prefixIcon"
            [suffixIcon]="_definition.input.suffixIcon"
            [enterSubmit]="false"
            (event)="onValueChange($event)"
            [class.mb-2]="
              internalControl.invalid && (internalControl.dirty || internalControl.touched)
            ">
          </oiq-form-input>
        }
      }
    }
  `,
})
export class CollectionFilterInputComponent extends CollectionTableFilterBaseComponent {
  internalControl = new FormControl('');
  phoneControl = new FormControl('', [OiqFormPhoneLengthValidator]);
  emailControl = new FormControl('', [Validators.email]);

  constructor() {
    super();
    effect(() => {
      const _value = this.value();
      const _definition = this.definition();
      if (!_definition) {
        return;
      }

      const _normalized = CollectionFilterFromQueryInput(_value, _definition, false) as any;

      switch (_definition.input.type) {
        case 'phone':
          if (this.phoneControl.value === _normalized) {
            return;
          }
          break;
        case 'email':
          if (this.emailControl.value === _normalized) {
            return;
          }
          break;
        default:
          if (this.internalControl.value === _normalized) {
            return;
          }
      }

      switch (_definition.input.type) {
        case 'phone':
          this.phoneControl.setValue(_normalized, { emitEvent: false });
          break;
        case 'email':
          this.emailControl.setValue(_normalized, { emitEvent: false });
          break;
        default:
          this.internalControl.setValue(_normalized, { emitEvent: false });
      }
    });
  }

  getQuery() {
    switch (this.definition().input.type) {
      case 'phone':
        if (this.phoneControl.invalid) {
          return null;
        }
        return CollectionFilterFormatPayloadInput(this.phoneControl.value, this.definition());
      case 'email':
        if (this.emailControl.invalid) {
          return null;
        }
        return CollectionFilterFormatPayloadInput(this.emailControl.value, this.definition());
      default:
        return CollectionFilterFormatPayloadInput(this.internalControl.value, this.definition());
    }
  }

  onValueChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'blur':
      case 'enter':
        this.handleValueChanged();
        break;
      case 'clear':
        // Manually clear the appropriate FormControl
        this.handleValueChanged();
        break;
    }
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQueryInput(query, definition, true) as { [key: string]: any };
  }
}
